<template>
    <div ref="container" id="surveyModelContainer" class="w-full h-full relative rounded-lg overflow-hidden bg-black">
        <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center z-10 bg-black bg-opacity-50">
            <div class="flex flex-col items-center text-white">
                <!-- Radial Progress Loader -->
                <div class="relative w-20 h-20 mb-4">
                    <!-- Background circle -->
                    <svg class="w-20 h-20 transform -rotate-90" viewBox="0 0 80 80">
                        <circle cx="40" cy="40" r="34" stroke="#374151" stroke-width="6" fill="none" />
                        <!-- Progress circle -->
                        <circle 
                            cx="40" 
                            cy="40" 
                            r="34" 
                            stroke="#27bdf4" 
                            stroke-width="6" 
                            fill="none" 
                            stroke-linecap="round"
                            :stroke-dasharray="213.6"
                            :stroke-dashoffset="213.6 - (213.6 * loadingProgress / 100)"
                            class="transition-all duration-300 ease-out"
                        />
                    </svg>
                    <!-- Progress percentage -->
                    <div class="absolute inset-0 flex items-center justify-center">
                        <span class="text-lg font-bold text-primary">{{ Math.round(loadingProgress) }}%</span>
                    </div>
                </div>
                <p class="text-sm text-center">Loading 3D Reef Model...</p>
            </div>
        </div>
        <div v-if="hasError" class="absolute inset-0 flex items-center justify-center z-10 bg-black bg-opacity-90">
            <div class="text-center text-white">
                <i class="mdi mdi-alert-circle text-4xl text-red-400 mb-4"></i>
                <p class="text-lg font-medium mb-2">Model not found</p>
                <p class="text-sm text-neutral-400">Unable to load the 3D model for this survey</p>
            </div>
        </div>
        
        
    </div>
</template>

<script>
import * as THREE from 'three';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { ViewportGizmo } from 'three-viewport-gizmo';
import { EventBus } from '../event-bus';
import { storage_resto } from '../main';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';

export default {
    name: 'SurveyModel',
    props: {
        dataPath: {
            type: String,
            required: true
        },
        aiEnabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            scene: null,
            camera: null,
            renderer: null,
            controls: null,
            mesh: null,
            isLoading: true,
            hasError: false,
            loadingProgress: 0,
            modelSize: null,
            gizmo: null,
            cameraMarkersGroup: null,
            raycaster: null,
            mouse: null,
            meshCenter: new THREE.Vector3(),
            showCameras: false,
            cameraData: [], // Store camera data without creating 3D objects
            camerasCreated: false,
            selectedCameraId: null, // Track selected camera for highlighting
            hoveredCameraId: null, // Track hovered camera from gallery
            globalTransform: {
                rotation: {
                    x: Math.PI / 2, // Rotate -90 degrees to convert from Z-up to Y-up
                    y: 0,
                    z: 0
                },
                position: {
                    x: 0,
                    y: 0,
                    z: 0
                }
            },
            // Spatial Intelligence toggle
            spatialIntelligenceEnabled: false,
            // Semantic class color mapping based on Coralscapes dataset
            semanticClassColors: {
                // Official Coralscapes dataset color mapping (RGB values)
                'human': '#FF0000',                    // [255, 0, 0]
                'background': '#1DA2D8',               // [29, 162, 216]
                'fish': '#FFFF00',                     // [255, 255, 0]
                'sand': '#C2B280',                     // [194, 178, 128]
                'rubble': '#A19980',                   // [161, 153, 128]
                'unknown hard substrate': '#7D7D7D',  // [125, 125, 125]
                'algae covered substrate': '#7DA37D', // [125, 163, 125]
                'dark': '#1F1F1F',                     // [31, 31, 31]
                'branching bleached': '#FCE7F0',      // [252, 231, 240]
                'branching dead': '#7B3256',          // [123, 50, 86]
                'branching alive': '#E25B9D',          // [226, 91, 157]
                'stylophora alive': '#FF6FC2',         // [255, 111, 194]
                'pocillopora alive': '#FF9296',       // [255, 146, 150]
                'acropora alive': '#EC80FF',          // [236, 128, 255]
                'table acropora alive': '#BD77FF',    // [189, 119, 255]
                'table acropora dead': '#553574',     // [85, 53, 116]
                'millepora': '#F49673',               // [244, 150, 115]
                'turbinaria': '#E4FF77',              // [228, 255, 119]
                'other coral bleached': '#FAE0E1',    // [250, 224, 225]
                'other coral dead': '#723C3D',        // [114, 60, 61]
                'other coral alive': '#E07677',       // [224, 118, 119]
                'massive/meandering alive': '#EC9615', // [236, 150, 21]
                'massive/meandering dead': '#865612', // [134, 86, 18]
                'massive/meandering bleached': '#FFF8E4', // [255, 248, 228]
                'meandering alive': '#E6C100',        // [230, 193, 0]
                'meandering dead': '#77640E',         // [119, 100, 14]
                'meandering bleached': '#FBF3D8',     // [251, 243, 216]
                'transect line': '#00FF00',            // [0, 255, 0]
                'transect tools': '#08CD0C',           // [8, 205, 12]
                'sea urchin': '#008EFF',              // [0, 142, 255]
                'sea cucumber': '#00E7FF',            // [0, 231, 255]
                'anemone': '#00FFBD',                 // [0, 255, 189]
                'sponge': '#F05050',                  // [240, 80, 80]
                'clam': '#BDFFEA',                    // [189, 255, 234]
                'other animal': '#00FFFF',            // [0, 255, 255]
                'trash': '#FF0086',                   // [255, 0, 134]
                'seagrass': '#7DDE7D',                // [125, 222, 125]
                'crown of thorn': '#B3F5EA',          // [179, 245, 234]
                'dead clam': '#599B86'                // [89, 155, 134]
            },
            // Store original materials for restoration
            originalMaterials: new Map(),
            // Store class metadata for color application
            classMetadata: null,
            // Semantic overlay opacity (0.0 to 1.0)
            semanticOverlayOpacity: 0.3
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.initThree();
            this.animate();
            window.addEventListener('resize', this.handleResize);
        });
        
        // Listen for gallery image hover events
        EventBus.$on('gallery-image-hover', this.onGalleryImageHover);
        EventBus.$on('gallery-image-leave', this.onGalleryImageLeave);
        EventBus.$on('toggle-cameras', this.toggleCameras);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        
        // Remove interaction event listeners
        if (this.$refs.container) {
            this.$refs.container.removeEventListener('click', this.onMouseClick);
            this.$refs.container.removeEventListener('mousemove', this.onMouseMove);
        }
        
        // Clean up camera data
        this.destroyCameraMarkers();
        this.cameraData = [];
        
        // Remove event bus listeners
        EventBus.$off('gallery-image-hover', this.onGalleryImageHover);
        EventBus.$off('gallery-image-leave', this.onGalleryImageLeave);
        EventBus.$off('toggle-cameras', this.toggleCameras);
        
        if (this.renderer) {
            this.renderer.dispose();
        }
        if (this.gizmo) {
            this.gizmo.dispose();
        }
        if (this.scene) {
            this.scene.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (child.material.map) child.material.map.dispose();
                        child.material.dispose();
                    }
                }
            });
        }
    },
    computed: {
    },
    methods: {
        initThree() {
            // Create scene
            this.scene = new THREE.Scene();
            this.scene.background = null;
            
            // Create camera with narrower field of view
            this.camera = new THREE.PerspectiveCamera(
                45, 
                this.$refs.container.clientWidth / this.$refs.container.clientHeight, 
                0.1, 
                1000
            );
            this.camera.up.set(0, 0, 1);
            
            // Create renderer
            this.renderer = new THREE.WebGLRenderer({ 
                antialias: true,
                alpha: true
            });
            this.renderer.setPixelRatio(window.devicePixelRatio);
            this.renderer.setSize(this.$refs.container.clientWidth, this.$refs.container.clientHeight);
            // this.renderer.autoClear = false;
            
            // Style the canvas
            this.renderer.domElement.style.borderRadius = '0.5rem';
            this.$refs.container.appendChild(this.renderer.domElement);

            // Add lighting
            const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
            this.scene.add(ambientLight);
            
            const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
            directionalLight.position.set(10, 10, 10);
            directionalLight.castShadow = true;
            directionalLight.shadow.mapSize.width = 2048;
            directionalLight.shadow.mapSize.height = 2048;
            this.scene.add(directionalLight);

            // Add OrbitControls
            this.controls = new OrbitControls(this.camera, this.renderer.domElement);
            this.controls.enableDamping = true;
            this.controls.dampingFactor = 0.05;
            this.controls.enableZoom = true;
            this.controls.autoRotate = false;
            this.controls.autoRotateSpeed = 1.0;

            // Setup camera markers and interaction
            this.setupInteraction();

            this.loadModel();
        },
        
        async loadModel() {
            if (!this.dataPath) {
                this.hasError = true;
                this.isLoading = false;
                return;
            }

            this.isLoading = true;
            this.hasError = false;
            this.loadingProgress = 0;
            
            try {
                // Get the download URL from Firebase Storage
                const glbModelUrl = await this.getGLBDownloadUrl();
                
                // Load the GLB model
                this.loadGLBModel(glbModelUrl);
            } catch (error) {
                console.error('Error getting GLB download URL:', error);
                this.hasError = true;
                this.isLoading = false;
            }
        },
        
        async getGLBDownloadUrl() {
            // Build the storage path: dataPath/output/output.glb
            console.log('Data path:', this.dataPath);
            const storagePath = `${this.dataPath}/output/output.glb`;
            
            console.log('Getting download URL for:', storagePath);
            
            // Get reference to the file in Firebase Storage
            const fileRef = storageRef(storage_resto, storagePath);
            
            // Get the download URL
            const downloadUrl = await getDownloadURL(fileRef);
            
            return downloadUrl;
        },

        loadGLBModel(glbModelUrl) {
            const gltfLoader = new GLTFLoader();
            
            console.log('Loading GLB model from:', glbModelUrl);
            
            gltfLoader.load(
                glbModelUrl,
                (gltf) => {
                    console.log('GLB model loaded successfully');
                    
                    // Parse GLB for _class attribute and metadata
                    this.parseGLBClassData(gltf);
                    
                    // Get the scene from the GLTF file
                    this.mesh = gltf.scene;
                    
                    // Apply global transform
                    this.applyGlobalTransform(this.mesh);
                    this.scene.add(this.mesh);
                    
                    this.centerAndPositionModel();
                    this.positionCamera();
                    this.addViewportGizmo();
                    
                    // Load camera positions after main mesh is loaded
                    this.loadCameraPositions();
                    
                    this.isLoading = false;
                },
                (progress) => {
                    if (progress.lengthComputable) {
                        this.loadingProgress = (progress.loaded / progress.total) * 100;
                    }
                },
                (error) => {
                    console.error('Error loading GLB model:', error);
                    this.isLoading = false;
                    this.hasError = true;
                }
            );
        },

        parseGLBClassData(gltf) {
            console.log('=== Parsing GLB Class Data ===');
            
            // Extract metadata from GLTF extras
            let classMetadata = null;
            if (gltf.parser && gltf.parser.json && gltf.parser.json.extras) {
                const extras = gltf.parser.json.extras;
                console.log('Found GLTF extras metadata:', extras);
                
                // Check if class_mapping exists in extras
                if (extras.class_mapping) {
                    classMetadata = extras.class_mapping;
                    this.classMetadata = classMetadata; // Store for later use
                    console.log('Found class_mapping in extras:', classMetadata);
                }
            }
            
            // Also check scene extras
            if (gltf.scene && gltf.scene.userData && gltf.scene.userData.extras) {
                const extras = gltf.scene.userData.extras;
                console.log('Found scene userData extras:', extras);
                
                if (extras.class_mapping) {
                    classMetadata = extras.class_mapping;
                    this.classMetadata = classMetadata; // Store for later use
                    console.log('Found class_mapping in scene extras:', classMetadata);
                }
            }
            
            // Traverse the scene to find meshes and extract _class attribute
            gltf.scene.traverse((child) => {
                if (child.isMesh && child.geometry) {
                    console.log('Found mesh:', child.name || 'unnamed');
                    
                    // Store original material for restoration
                    if (child.material) {
                        this.originalMaterials.set(child.uuid, child.material.clone());
                    }
                    
                    // Check if geometry has attributes
                    if (child.geometry.attributes) {
                        console.log('Available attributes:', Object.keys(child.geometry.attributes));
                        
                        // Look for _class attribute
                        if (child.geometry.attributes._class) {
                            const classAttribute = child.geometry.attributes._class;
                            console.log('Found _class attribute:');
                            console.log('- Type:', classAttribute.type);
                            console.log('- Item size:', classAttribute.itemSize);
                            console.log('- Count:', classAttribute.count);
                            
                            // Extract the actual class values
                            const classArray = classAttribute.array;
                            const uniqueClasses = [...new Set(classArray)];
                            console.log('- Unique class values:', uniqueClasses);
                            
                            // Count occurrences of each class
                            const classCounts = {};
                            uniqueClasses.forEach(cls => {
                                classCounts[cls] = classArray.filter(val => val === cls).length;
                            });
                            console.log('- Class distribution:', classCounts);
                            
                            // If we have metadata, try to map class IDs to names
                            if (classMetadata && typeof classMetadata === 'object') {
                                console.log('=== Class ID to Name Mapping ===');
                                uniqueClasses.forEach(classId => {
                                    const className = classMetadata[classId] || classMetadata[classId.toString()];
                                    if (className) {
                                        console.log(`Class ID ${classId}: ${className} (${classCounts[classId]} points)`);
                                    } else {
                                        console.log(`Class ID ${classId}: Unknown (${classCounts[classId]} points)`);
                                    }
                                });
                            } else {
                                console.log('No class_mapping metadata found to map class IDs to names');
                            }
                        } else {
                            console.log('No _class attribute found in this mesh');
                        }
                    } else {
                        console.log('No attributes found in geometry');
                    }
                }
            });
            
            console.log('=== End GLB Class Data Parsing ===');
        },
        
        applyGlobalTransform(mesh) {
            // Apply global rotation
            mesh.rotation.x = this.globalTransform.rotation.x;
            mesh.rotation.y = this.globalTransform.rotation.y;
            mesh.rotation.z = this.globalTransform.rotation.z;
            
            // Center the mesh
            const box = new THREE.Box3().setFromObject(mesh);
            const center = new THREE.Vector3();
            box.getCenter(center);
            
            // Store the mesh center for camera positioning
            this.meshCenter.copy(center);
            
            mesh.position.copy(center).negate();
            
            // Apply any additional global position offset
            mesh.position.x += this.globalTransform.position.x;
            mesh.position.y += this.globalTransform.position.y;
            mesh.position.z += this.globalTransform.position.z;
        },

        centerAndPositionModel() {
            // Calculate model size for camera positioning
            const box = new THREE.Box3().setFromObject(this.mesh);
            box.getSize(this.modelSize = new THREE.Vector3());
        },
        
        positionCamera() {
            const maxDimension = Math.max(this.modelSize.x, this.modelSize.y, this.modelSize.z);
            const distance = maxDimension / (2 * Math.tan(this.camera.fov * Math.PI / 360)) * 0.4;
            const angleDistance = distance * 0.7;
            
            this.camera.position.set(angleDistance, distance * 0.8, angleDistance);
            this.camera.lookAt(0, 0, 0);
            this.controls.target.set(0, 0, 0);
            this.controls.update();
        },
        
        addViewportGizmo() {
            this.gizmo = new ViewportGizmo(this.camera, this.renderer, {
                placement: 'bottom-right',
                size: 80,
                container: this.$refs.container
            });
            this.gizmo.attachControls(this.controls);
        },
        
        animate() {
            requestAnimationFrame(this.animate);
            
            if (this.controls) {
                this.controls.update();
            }
            
            // Update camera marker scales with smooth transitions
            if (this.cameraMarkersGroup) {
                this.cameraMarkersGroup.children.forEach(marker => {
                    if (!marker.userData.isHovered && marker.scale.x > 1) {
                        marker.scale.lerp(marker.userData.originalScale, 0.1);
                    }
                });
            }
            
            if (this.renderer && this.scene && this.camera) {
                // Render main scene
                this.renderer.render(this.scene, this.camera);
                
                // Render gizmo overlay
                if (this.gizmo) {
                    this.gizmo.render();
                }
            }
        },
        
        handleResize() {
            if (!this.camera || !this.renderer || !this.$refs.container) return;
            
            this.camera.aspect = this.$refs.container.clientWidth / this.$refs.container.clientHeight;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(this.$refs.container.clientWidth, this.$refs.container.clientHeight);
            
            // Update gizmo for new viewport size
            if (this.gizmo) {
                this.gizmo.update();
            }
        },

        setupInteraction() {
            this.raycaster = new THREE.Raycaster();
            this.mouse = new THREE.Vector2();
            
            // Create camera markers group
            this.cameraMarkersGroup = new THREE.Group();
            this.cameraMarkersGroup.visible = this.showCameras; // Start hidden
            this.scene.add(this.cameraMarkersGroup);
            
            // Add event listeners
            this.$refs.container.addEventListener('click', this.onMouseClick);
            this.$refs.container.addEventListener('mousemove', this.onMouseMove);
        },

        async loadCameraPositions() {
            try {
                // Get the download URL for the COLMAP images.txt file from Firebase Storage
                const storagePath = `${this.dataPath}/output/colmap/sparse/0/images.txt`;
                console.log('Loading camera positions from:', storagePath);
                
                const fileRef = storageRef(storage_resto, storagePath);
                const downloadUrl = await getDownloadURL(fileRef);
                
                // Fetch the file content
                const response = await fetch(downloadUrl);
                const text = await response.text();
                
                const lines = text.split('\n');
                this.cameraData = []; // Reset camera data

                for (const line of lines) {
                    // Skip comments and empty lines
                    if (line.startsWith('#') || line.trim() === '') continue;
                    
                    const parts = line.trim().split(/\s+/);
                    if (parts.length < 8) continue; // Need at least 8 parts for camera data
                    
                    // Parse COLMAP format: IMAGE_ID QW QX QY QZ TX TY TZ CAMERA_ID NAME ...
                    const imageId = parseInt(parts[0]);
                    const qw = parseFloat(parts[1]);
                    const qx = parseFloat(parts[2]);
                    const qy = parseFloat(parts[3]);
                    const qz = parseFloat(parts[4]);
                    const tx = parseFloat(parts[5]);
                    const ty = parseFloat(parts[6]);
                    const tz = parseFloat(parts[7]);
                    const imageName = parts[9] || `image_${imageId}`;

                    // Create quaternion from COLMAP data
                    const quaternion = new THREE.Quaternion(qx, qy, qz, qw);
                    
                    // Create position vector
                    const position = new THREE.Vector3(tx, ty, tz);
                    
                    // COLMAP uses camera-to-world transformation, but we need world position
                    // Convert from camera pose to world position by inverting the transformation
                    const rotationMatrix = new THREE.Matrix4();
                    rotationMatrix.makeRotationFromQuaternion(quaternion);
                    
                    // Invert rotation and apply to translation to get world position
                    const invertedRotation = rotationMatrix.clone().transpose();
                    const worldPosition = position.clone().negate();
                    worldPosition.applyMatrix4(invertedRotation);
                    
                    // Invert the quaternion for world orientation
                    const worldQuaternion = quaternion.clone().invert();
                    
                    // Additional rotation to correct camera orientation (COLMAP cameras point toward +Z, we want -Z)
                    const correctionRotation = new THREE.Quaternion();
                    correctionRotation.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI);
                    worldQuaternion.multiplyQuaternions(worldQuaternion, correctionRotation);

                    // Apply the same translation as the mesh (subtract mesh center)
                    const adjustedPosition = worldPosition.clone().sub(this.meshCenter);

                    // Store camera data instead of creating 3D objects immediately
                    this.cameraData.push({
                        position: adjustedPosition,
                        quaternion: worldQuaternion,
                        label: imageName,
                        imageId: imageId
                    });
                }

                console.log(`Loaded ${this.cameraData.length} cameras from COLMAP data (data only, no 3D objects)`);
            } catch (error) {
                console.error('Error loading or parsing COLMAP images.txt:', error);
            }
        },

        createCameraMarkers() {
            if (this.camerasCreated || this.cameraData.length === 0) return;
            
            console.log(`Creating 3D objects for ${this.cameraData.length} cameras`);
            
            // COLMAP camera parameters from cameras.txt:
            // 0 SIMPLE_PINHOLE 1080 1920 2281.5300 542.084 956.682
            const cameraWidth = 1080;    // pixels
            const cameraHeight = 1920;   // pixels
            const focalLength = 2281.53; // pixels
            
            // Calculate field of view from focal length and sensor dimensions
            const fovHorizontal = 2 * Math.atan(cameraWidth / (2 * focalLength));
            const fovVertical = 2 * Math.atan(cameraHeight / (2 * focalLength));
            const aspectRatio = cameraWidth / cameraHeight;
            
            console.log(`Camera FOV: ${(fovHorizontal * 180 / Math.PI).toFixed(1)}° horizontal, ${(fovVertical * 180 / Math.PI).toFixed(1)}° vertical`);
            
            // Create realistic camera frustum geometry using CylinderGeometry as base
            const createFrustumGeometry = () => {
                // Frustum depth (length of the pyramid)
                const frustumDepth = 1;
                
                // Calculate frustum dimensions at the far plane based on actual FOV
                const halfWidth = Math.tan(fovHorizontal / 2) * frustumDepth;
                const halfHeight = Math.tan(fovVertical / 2) * frustumDepth;
                
                // Create a rectangular pyramid using ConeGeometry as base
                // We'll use the larger dimension to create a square pyramid, then scale to aspect ratio
                const maxDimension = Math.max(halfWidth, halfHeight);
                const pyramidGeometry = new THREE.ConeGeometry(maxDimension, frustumDepth, 4);
                
                // Scale to match actual camera aspect ratio, but make them less tall
                const heightScale = (halfHeight / maxDimension) * 0.25; // Make 50% less tall
                pyramidGeometry.scale(halfWidth / maxDimension, heightScale, 1);
                
                // Rotate so the tip points down the negative Z axis (camera forward direction)
                pyramidGeometry.rotateX(Math.PI / 2);
                pyramidGeometry.translate(0, 0, -frustumDepth / 2);
                
                // Create edges geometry for clean wireframe
                const edgesGeometry = new THREE.EdgesGeometry(pyramidGeometry);
                
                // Clean up intermediate geometry
                pyramidGeometry.dispose();
                
                return edgesGeometry;
            };
            
            // Create shared geometries and materials
            const frustumGeometry = createFrustumGeometry();
            const normalMaterial = new THREE.LineBasicMaterial({ 
                color: 0xaaaaaa, // Light gray
                transparent: true,
                opacity: 0.6,
                linewidth: 2
            });
            const selectedMaterial = new THREE.LineBasicMaterial({ 
                color: 0x27bdf4, // Primary color for selected
                transparent: true,
                opacity: 0.9,
                linewidth: 3
            });
            const hoveredMaterial = new THREE.LineBasicMaterial({ 
                color: 0x27bdf4, // Primary color for hovered
                transparent: true,
                opacity: 0.7,
                linewidth: 2
            });

            // Create camera markers using shared geometry/materials
            this.cameraData.forEach((cameraInfo) => {
                const group = new THREE.Group();
                
                // Create frustum using wireframe lines (no dot/sphere)
                const frustum = new THREE.LineSegments(frustumGeometry, normalMaterial);
                group.add(frustum);
                
                // Set position and orientation
                group.position.copy(cameraInfo.position);
                group.quaternion.copy(cameraInfo.quaternion);
                
                // Store metadata and materials for switching
                group.userData.cameraLabel = cameraInfo.label;
                group.userData.imageId = cameraInfo.imageId;
                group.userData.originalScale = group.scale.clone();
                group.userData.isHovered = false;
                group.userData.normalMaterial = normalMaterial;
                group.userData.selectedMaterial = selectedMaterial;
                group.userData.hoveredMaterial = hoveredMaterial;
                group.userData.frustum = frustum; // Reference to the frustum for material changes
                
                this.cameraMarkersGroup.add(group);
            });
            
            this.camerasCreated = true;
            console.log('Camera 3D objects created with realistic FOV and 3x scale');
        },

        destroyCameraMarkers() {
            if (!this.camerasCreated) return;
            
            console.log('Destroying camera 3D objects');
            
            // Remove all camera markers from the group
            while (this.cameraMarkersGroup.children.length > 0) {
                this.cameraMarkersGroup.remove(this.cameraMarkersGroup.children[0]);
            }
            
            this.camerasCreated = false;
            console.log('Camera 3D objects destroyed');
        },

        onMouseClick(event) {
            if (!this.raycaster || !this.mouse || !this.camera || !this.renderer) return;
            
            // Don't process clicks if cameras are not visible
            if (!this.showCameras) return;
            
            // Use the canvas element directly for more accurate coordinates
            const canvas = this.renderer.domElement;
            const rect = canvas.getBoundingClientRect();
            
            this.mouse.x = ((event.clientX - rect.left) / canvas.clientWidth) * 2 - 1;
            this.mouse.y = -((event.clientY - rect.top) / canvas.clientHeight) * 2 + 1;

            this.raycaster.setFromCamera(this.mouse, this.camera);
            const intersects = this.raycaster.intersectObjects(this.cameraMarkersGroup.children, true);

            if (intersects.length > 0) {
                const clickedMarker = intersects[0].object.parent;
                const imageId = clickedMarker.userData.imageId;
                const cameraLabel = clickedMarker.userData.cameraLabel;
                
                console.log('Clicked camera:', cameraLabel);
                
                this.selectedCameraId = imageId;
                this.hoveredCameraId = null;
                this.updateCameraHighlights();
                
                EventBus.$emit('camera-selected', {
                    imageId: imageId,
                    cameraLabel: cameraLabel
                });
            } else {
                this.selectedCameraId = null;
                this.updateCameraHighlights();
                EventBus.$emit('camera-deselected');
            }
        },

        onMouseMove(event) {
            if (!this.raycaster || !this.mouse || !this.camera || !this.renderer) return;
            
            // Don't process hover if cameras are not visible
            if (!this.showCameras) return;
            
            // Use the canvas element directly for more accurate coordinates
            const canvas = this.renderer.domElement;
            const rect = canvas.getBoundingClientRect();
            
            this.mouse.x = ((event.clientX - rect.left) / canvas.clientWidth) * 2 - 1;
            this.mouse.y = -((event.clientY - rect.top) / canvas.clientHeight) * 2 + 1;

            this.raycaster.setFromCamera(this.mouse, this.camera);
            const intersects = this.raycaster.intersectObjects(this.cameraMarkersGroup.children, true);

            // Reset hover state for all markers
            this.cameraMarkersGroup.children.forEach(marker => {
                marker.userData.isHovered = false;
            });

            // Scale up hovered marker
            if (intersects.length > 0) {
                const hoveredMarker = intersects[0].object.parent;
                hoveredMarker.userData.isHovered = true;
                hoveredMarker.scale.setScalar(1.5);
                canvas.style.cursor = 'pointer';
            } else {
                canvas.style.cursor = 'default';
            }
        },

        toggleCameras() {
            this.showCameras = !this.showCameras;
            
            if (this.showCameras) {
                // Lazy loading: create camera objects only when first shown
                this.createCameraMarkers();
                this.cameraMarkersGroup.visible = true;
            } else {
                // Hide cameras but keep objects in memory for faster re-showing
                this.cameraMarkersGroup.visible = false;
                // Clear selection when hiding cameras
                this.selectedCameraId = null;
                this.hoveredCameraId = null;
                EventBus.$emit('camera-deselected');
                // Optionally destroy objects to free memory (uncomment next line)
                // this.destroyCameraMarkers();
            }
            
            // Emit camera visibility state to gallery
            EventBus.$emit('cameras-visibility-changed', this.showCameras);
        },

        updateCameraHighlights() {
            if (!this.cameraMarkersGroup) return;
            
            this.cameraMarkersGroup.children.forEach(cameraGroup => {
                const imageId = cameraGroup.userData.imageId;
                const frustum = cameraGroup.userData.frustum;
                
                if (imageId === this.selectedCameraId) {
                    // Selected camera - use primary color with high opacity
                    frustum.material = cameraGroup.userData.selectedMaterial;
                } else if (imageId === this.hoveredCameraId) {
                    // Hovered camera - use primary color with medium opacity
                    frustum.material = cameraGroup.userData.hoveredMaterial;
                } else {
                    // Normal camera - use gray
                    frustum.material = cameraGroup.userData.normalMaterial;
                }
            });
        },

        onGalleryImageHover(data) {
            // Find camera by matching filename
            this.hoveredCameraId = this.findCameraIdByFilename(data.filename);
            // Clear selection when hovering over different camera
            if (this.hoveredCameraId && this.hoveredCameraId !== this.selectedCameraId) {
                this.selectedCameraId = null;
                EventBus.$emit('camera-deselected');
            }
            this.updateCameraHighlights();
        },

        onGalleryImageLeave() {
            this.hoveredCameraId = null;
            this.updateCameraHighlights();
        },

        findCameraIdByFilename(filename) {
            // Match gallery image filename with camera data
            for (const camera of this.cameraData) {
                if (camera.label === filename) {
                    return camera.imageId;
                }
            }
            return null;
        },

        toggleSpatialIntelligence() {
            this.spatialIntelligenceEnabled = !this.spatialIntelligenceEnabled;
            
            if (this.spatialIntelligenceEnabled) {
                this.applySemanticColors();
            } else {
                this.restoreOriginalColors();
            }
            
            console.log('Spatial Intelligence:', this.spatialIntelligenceEnabled ? 'Enabled' : 'Disabled');
        },

        applySemanticColors() {
            if (!this.mesh || !this.classMetadata) {
                console.warn('No mesh or class metadata available for semantic coloring');
                return;
            }

            console.log('Applying semantic colors as overlay...');
            
            this.mesh.traverse((child) => {
                if (child.isMesh && child.geometry && child.geometry.attributes._class) {
                    const classAttribute = child.geometry.attributes._class;
                    const classArray = classAttribute.array;
                    
                    // Create semantic color array for vertices
                    const semanticColors = new Float32Array(classArray.length * 3);
                    
                    for (let i = 0; i < classArray.length; i++) {
                        const classId = classArray[i];
                        const className = this.classMetadata[classId] || this.classMetadata[classId.toString()];
                        const color = this.semanticClassColors[className] || '#FFFFFF';
                        
                        // Convert hex color to RGB
                        const rgb = this.hexToRgb(color);
                        
                        semanticColors[i * 3] = rgb.r / 255;     // R
                        semanticColors[i * 3 + 1] = rgb.g / 255; // G
                        semanticColors[i * 3 + 2] = rgb.b / 255; // B
                    }
                    
                    // Add semantic color attribute to existing geometry
                    child.geometry.setAttribute('semanticColor', new THREE.BufferAttribute(semanticColors, 3));
                    
                    // Create a custom shader material that blends original colors with semantic colors
                    const material = new THREE.ShaderMaterial({
                        uniforms: {
                            semanticOpacity: { value: this.semanticOverlayOpacity }, // Control overlay opacity
                            useSemanticColors: { value: true }
                        },
                        vertexShader: `
                            attribute vec3 color;
                            attribute vec3 semanticColor;
                            varying vec3 vOriginalColor;
                            varying vec3 vSemanticColor;
                            
                            void main() {
                                vOriginalColor = color;
                                vSemanticColor = semanticColor;
                                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                            }
                        `,
                        fragmentShader: `
                            uniform float semanticOpacity;
                            uniform bool useSemanticColors;
                            varying vec3 vOriginalColor;
                            varying vec3 vSemanticColor;
                            
                            void main() {
                                if (useSemanticColors) {
                                    // Blend original color with semantic color using opacity
                                    vec3 blendedColor = mix(vOriginalColor, vSemanticColor, semanticOpacity);
                                    gl_FragColor = vec4(blendedColor, 1.0);
                                } else {
                                    gl_FragColor = vec4(vOriginalColor, 1.0);
                                }
                            }
                        `,
                        side: THREE.DoubleSide
                    });
                    
                    // Apply the new material
                    child.material = material;
                    
                    console.log(`Applied semantic color overlay to mesh: ${child.name || 'unnamed'}`);
                }
            });
        },

        restoreOriginalColors() {
            if (!this.mesh) {
                console.warn('No mesh available for color restoration');
                return;
            }

            console.log('Restoring original colors...');
            
            // Restore original materials
            this.mesh.traverse((child) => {
                if (child.isMesh && this.originalMaterials.has(child.uuid)) {
                    // Restore original material
                    child.material = this.originalMaterials.get(child.uuid);
                    
                    // Remove semantic color attribute if it exists
                    if (child.geometry.attributes.semanticColor) {
                        child.geometry.deleteAttribute('semanticColor');
                    }
                    
                    console.log(`Restored original material for mesh: ${child.name || 'unnamed'}`);
                }
            });
        },

        hexToRgb(hex) {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : { r: 255, g: 255, b: 255 };
        },

        updateSemanticOverlayOpacity(opacity) {
            this.semanticOverlayOpacity = Math.max(0, Math.min(1, opacity)); // Clamp between 0 and 1
            
            // Update existing semantic materials if they exist
            if (this.mesh && this.spatialIntelligenceEnabled) {
                this.mesh.traverse((child) => {
                    if (child.isMesh && child.material && child.material.uniforms && child.material.uniforms.semanticOpacity) {
                        child.material.uniforms.semanticOpacity.value = this.semanticOverlayOpacity;
                    }
                });
            }
        }
    },
    
    watch: {
        aiEnabled(newValue) {
            console.log('aiEnabled', newValue);
            // Toggle spatial intelligence when AI is enabled/disabled
            if (newValue !== this.spatialIntelligenceEnabled) {
                this.toggleSpatialIntelligence();
            }
        },
        dataPath: {
            handler() {
                if (this.scene && this.mesh) {
                    // Remove old mesh
                    this.scene.remove(this.mesh);
                    // Dispose of geometry and materials in the GLB scene
                    this.mesh.traverse((child) => {
                        if (child instanceof THREE.Mesh) {
                            if (child.geometry) child.geometry.dispose();
                            if (child.material) {
                                if (Array.isArray(child.material)) {
                                    child.material.forEach(material => material.dispose());
                                } else {
                                    child.material.dispose();
                                }
                            }
                        }
                    });
                    this.mesh = null;
                }
                
                // Reset loading state
                this.loadingProgress = 0;
                
                // Load new model
                this.loadModel();
            },
            immediate: false
        }
    }
};
</script>

<style scoped>
/* Radial progress loader styles are handled inline with Vue bindings */
</style>
